// The /threads conversation view — the flagship layout.
//
// LAYOUT CONTRACT (NEW_CHAT_PAGE_PLAN.md §1.1, amended by
// SIDEBAR_LENS_REWORK 2026-08-21, audit-critical):
//   · The page root is `h-full min-h-0 flex flex-col`; its top padding reads
//     --lens-topbar-h because the clearance is now for the page's OWN top bar
//     (ThreadsTopBar — absolute inside this relative root), which replaced
//     the dead navbar clearance. The bar holds the scope picker (moved out
//     of the composer) and the thread title.
//   · The transcript is the flex body and scrolls; the composer is the last
//     flex child and is anchored by the column. Nothing PAGE-LOCAL is
//     `position: fixed` — an animated route transform becomes the containing
//     block for fixed positioning (Main.jsx §70-78). The top bar IS fixed
//     (inside LensToolbar), which is exactly why /threads runs the
//     instant-swap staticVariants in Main.jsx: no transform ever lands on
//     this route's motion.div. Don't add fixed elements beyond the bar, and
//     don't move this route back to the animated variants.
//   · Messages live in a centred max-w-3xl column; the chrome spans the page.
//   · Citations open in the APP RIGHT SIDEBAR (supersedes the in-page
//     slide-over): it PUSHES via centerMarginRight, so the transcript
//     narrows but never unmounts. LayoutContext's route-change clear is no
//     hazard — citations open on clicks, long after that effect ran; the
//     unmount cleanup below keeps a source from leaking into other pages.
//
// The machinery — linkify/chips, the markdown block, the spinner-with-status,
// the smart autoscroll, the slide-over — is copy-adapted from the graph chat
// panel per the plan's copy strategy. Nothing is imported from it. What is
// deliberately NOT copied: turn compaction (a sidebar-space hack; here the
// full history scrolls), the per-entry graph focus, action cards and briefs.
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BookOpen,
  Check,
  Copy,
  Database,
  Globe,
  Link,
  Loader2,
  RotateCw,
  Waypoints,
  Wrench,
} from "lucide-react";

import { getDocIdData, getPeerDoc } from "../../../api/universal";
import { useRightSidebar } from "../../contexts/RightSidebarContext";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import { getTheme } from "../../Universal/useNavTheme";

import Composer from "./Composer";
import LensScopePicker from "./LensScopePicker";
import ThreadCitationPanel from "./ThreadCitationPanel";
import ThreadsTopBar from "./ThreadsTopBar";
import { citationComponents, linkifyCitations } from "./CitationChips";
import { NEW_UI } from "../../Navigation/constants";

// SIDEBAR_LENS_REWORK is GS chrome: DD keeps its navbar, so the top bar
// would render underneath it (opaque z-30 header vs this page's z-0 stack)
// and the old pt-14 clearance still applies there. DD gets the scope picker
// back in the composer row instead (the `scopePicker` slot below).
const isGS = NEW_UI; // new UI on every variant (see constants.js)

// Deterministic seeds — hardcoded on purpose (no fetch, no model): the empty
// state must be instant and identical every visit.
const SEED_QUESTIONS = [
  "What are the most significant developments in the last 24 hours?",
  "Which outbreaks are escalating right now, and where?",
  "Summarize this week's food-safety recalls and what drove them.",
  "What's changed in the reporting on a region I should be watching?",
];

// The server narrates the grounding phase before the first token — and, when a
// turn calls a tool, that tool's stage too (plan Part 6/7: every stage rides
// this same status event, carrying its human-readable text in `message`, which
// is why the corpus/citation/entity stages added with the 5-tool toolbox need
// no code here — this reads `message` generically and never switches on
// `stage`). This is a LABEL FOR THE SPINNER, never a gate on it: a server that
// sends no status event must render exactly the bare spinner. Tolerant of shape
// because a raw object reaching JSX is a React crash.
const statusText = (v) => {
  if (typeof v === "string") return v.trim().slice(0, 140);
  if (v && typeof v === "object") {
    return String(v.message || v.text || v.label || v.stage || "")
      .trim()
      .slice(0, 140);
  }
  return "";
};

// What each tool did, per tool (plan Part 7 correction #10). The `query` on a
// `tool_use` row is a DISPLAY STRING the server formats — a web query, a URL,
// "[2] Title", an entity name — so each tool needs its own verb, and the icon
// is what makes a stack of them readable at a glance. An unknown tool is a
// server newer than this bundle: it still gets a line ("Used"), because the
// fact that the answer used something is worth more than the name of it.
const TOOL_ANNOTATION = {
  search_web: { icon: Globe, label: "Searched", quote: true },
  search_corpus: { icon: Database, label: "Searched the corpus", quote: true },
  read_url: { icon: Link, label: "Read", quote: false },
  read_citation: { icon: BookOpen, label: "Re-read", quote: false },
  entity_lookup: { icon: Waypoints, label: "Looked up", quote: false },
};
const UNKNOWN_ANNOTATION = { icon: Wrench, label: "Used", quote: true };

// The tools a finished turn used (plan Part 6/7), from the persisted `tool_use`
// rows, GROUPED BY TOOL. Dedupe is WITHIN a tool and never across one: the same
// query twice is one fact, but the same string searched and then read are two
// different things and must not collapse into one. Blank display strings are
// dropped — a row with no query is a row with nothing to say. Groups come out
// in the order the turn first reached for each tool, which is the truth of what
// happened rather than an invented ranking.
//
// FINISHED TURNS ONLY, and that is the whole design of this annotation: while a
// turn streams, the server narrates each tool through status events and the
// spinner label carries it (a second, quieter line saying the same thing would
// be noise). `tool_use` is persisted, not streamed, so it reaches us on
// hydration — which is exactly when the status line is gone and the fact still
// needs to be on screen.
const toolAnnotations = (entry) => {
  if (entry?.status !== "done" && entry?.status !== "stopped") return [];
  if (!Array.isArray(entry.toolUse)) return [];
  const groups = new Map(); // tool -> Set(display strings), insertion-ordered
  for (const row of entry.toolUse) {
    const query = String(row?.query || "").trim();
    if (!query) continue;
    const tool = String(row?.tool || "").trim() || "unknown";
    if (!groups.has(tool)) groups.set(tool, new Set());
    groups.get(tool).add(query);
  }
  return [...groups].map(([tool, queries]) => ({
    tool,
    queries: [...queries],
  }));
};

const ThreadChatView = ({
  thread, // {thread_id, title, ...} | null
  isDraft, // no thread yet — the empty state and its seeds
  loading, // hydrating a selected thread
  loadError,
  transcript, // [{q, a, citations[], status, statusLine, error, truncated,
  //                suggestions[]}]
  busy, // last entry is streaming
  scope, // "global" | {kind, id, name, icon?, color?}
  scopeMissing, // the thread's saved lens no longer exists
  onScopeChange,
  webSearch, // composer web toggle — sticky per conversation (MainThreads)
  onWebSearchChange,
  onAsk,
  onStop,
  onRegenerate,
  dark,
}) => {
  const [copied, setCopied] = useState(null); // entry index, briefly
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const stickRef = useRef(true); // smart autoscroll: attached to bottom?
  const copyTimerRef = useRef(null);

  // Smart autoscroll: pin to bottom while streaming ONLY when the user is
  // already near it; scrolling up detaches, back down re-attaches. Auto-follow
  // is for GROWTH — a new turn or a streaming answer. A transcript identity
  // change that is not growth (a citations event landing, a re-render) must
  // never move the viewport, or reading an early turn yanks the user down.
  const prevLenRef = useRef(0);
  useEffect(() => {
    const el = scrollRef.current;
    const grew = transcript.length !== prevLenRef.current;
    prevLenRef.current = transcript.length;
    if (!el || !stickRef.current) return;
    if (!grew && !busy) return;
    el.scrollTop = el.scrollHeight;
  }, [transcript, busy]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const ask = useCallback(
    (q) => {
      stickRef.current = true; // asking is asking to see the answer
      onAsk(q);
    },
    [onAsk],
  );

  // Copy keeps the [n] markers: the chips are a render of them, and markdown
  // pasted elsewhere has to carry the citations it was making claims from.
  const copyAnswer = useCallback((entry, idx) => {
    navigator.clipboard?.writeText(entry.a || "");
    setCopied(idx);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(null), 1200);
  }, []);

  // Citations render in the app right sidebar (see the layout contract):
  // pushCitation replaces the sidebar's component and opens it; closing only
  // collapses the aside (the app-wide idiom — the component stays mounted).
  // The unmount cleanup mirrors the graph page's: the sidebar is app-level
  // and shared, so leaving /threads must not carry a source into other pages.
  const { setIsRightOpen, setRightComponent } = useRightSidebar();
  const t = getTheme(dark);
  // citationSeqRef gates every push (review 2026-08-21, two bugs one token):
  // the fetch can resolve AFTER the user closed the panel or left the page —
  // the setters are app-level context, so an ungated late response re-opens
  // the aside, on /threads or on whatever page they navigated to — and two
  // quick clicks race, letting the slower EARLIER document overwrite the one
  // the user is reading. Every open takes a fresh sequence number; close and
  // unmount invalidate all of them; a push whose number is stale is dropped.
  const citationSeqRef = useRef(0);
  const closeCitation = useCallback(() => {
    citationSeqRef.current += 1;
    setIsRightOpen(false);
  }, [setIsRightOpen]);
  const pushCitation = useCallback(
    (seq, state) => {
      if (seq !== citationSeqRef.current) return;
      setRightComponent(
        <ThreadCitationPanel state={state} onClose={closeCitation} t={t} />,
      );
      setIsRightOpen(true);
    },
    [setRightComponent, setIsRightOpen, closeCitation, t],
  );
  useEffect(
    () => () => {
      citationSeqRef.current += 1;
      setRightComponent(null);
      setIsRightOpen(false);
    },
    [setRightComponent, setIsRightOpen],
  );

  const openCitation = useCallback(
    (doc) => {
      const seq = ++citationSeqRef.current;
      if (doc.origin === "peer") {
        // Try the federation relay for the full body; the notice state is
        // the fallback (old peer / peer down / not public).
        pushCitation(seq, { loading: true, article: null });
        getPeerDoc(doc.documentId)
          .then((res) => {
            const body = res?.data?.doc || null;
            pushCitation(
              seq,
              body
                ? { loading: false, article: body, peer: true }
                : { loading: false, article: null, peerDoc: doc },
            );
          })
          .catch(() =>
            pushCitation(seq, { loading: false, article: null, peerDoc: doc }),
          );
        return;
      }
      pushCitation(seq, { loading: true, article: null });
      getDocIdData(doc.documentId)
        .then((res) =>
          pushCitation(seq, { loading: false, article: res?.data || null }),
        )
        .catch(() => pushCitation(seq, { loading: false, article: null }));
    },
    [pushCitation],
  );
  const lastIdx = transcript.length - 1;

  // Follow-ups for the LAST turn only, and only once that turn is finished:
  // they are an offer of what to ask next, so they belong to the end of the
  // conversation. `busy` takes them off screen the instant a new ask starts —
  // suggestions from the previous answer sitting above a question already in
  // flight would be pointing backwards.
  const lastEntry = transcript[lastIdx];
  const suggestions =
    !busy && lastEntry?.status === "done" && Array.isArray(lastEntry.suggestions)
      ? lastEntry.suggestions
      : [];

  return (
    // GS: top clearance is --lens-topbar-inset (12px bar top + measured
    // height + 16px gap, derived in useLensTopbarVar), live so a wrapping bar
    // pushes the transcript down with it; the transcript scroller keeps the
    // page's overflow-x guard. DD: the old navbar clearance, no bar.
    <div
      className={`relative flex h-full min-h-0 flex-col bg-mainBg font-poppins ${
        isGS ? "" : "pt-14"
      }`}
      style={isGS ? { paddingTop: "var(--lens-topbar-inset, 82px)" } : undefined}
    >
      {isGS && (
        <ThreadsTopBar
          scope={scope}
          scopeMissing={scopeMissing}
          onScopeChange={onScopeChange}
          disabled={busy}
          title={!isDraft ? thread?.title : null}
        />
      )}

      {/* Transcript — the flex body, and the only thing that scrolls.
          overflow-x-hidden: vertical is the ONLY scroll axis; long unbroken
          content must wrap (see break-words below), not widen the page. */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6"
      >
        <div className="mx-auto w-full max-w-3xl py-6">
          {loading ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2">
              <LoadingSpinner />
              <span className="text-xs text-mutedText">
                Loading conversation…
              </span>
            </div>
          ) : loadError ? (
            <div className="flex h-64 items-center justify-center text-xs text-rose-400">
              {loadError}
            </div>
          ) : transcript.length === 0 ? (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 text-center">
              <div className="space-y-1.5">
                <h1 className="text-[22px] font-light text-mainText">
                  What do you want to know?
                </h1>
                <p className="text-[12px] text-mutedText">
                  Answers are grounded in the corpus and cite the documents
                  behind every claim.
                </p>
              </div>
              <div className="grid w-full gap-2 sm:grid-cols-2">
                {SEED_QUESTIONS.map((seed) => (
                  <button
                    key={seed}
                    type="button"
                    onClick={() => ask(seed)}
                    className="rounded-xl border border-mainDivider bg-mainButtonBg/25 px-3 py-2.5 text-left text-[12px] text-mainText transition hover:border-velBlue/50 hover:bg-mainButtonBg/45"
                  >
                    {seed}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-7">
              {transcript.map((entry, i) => {
                const citations = entry.citations || [];
                const citedNs = new Set(citations.map((c) => c.n));
                const byN = new Map(citations.map((c) => [c.n, c]));
                const components = citationComponents(byN, openCitation);
                // Only BEFORE the first token: once prose is arriving the
                // answer is its own progress, and a stale "searching…" under a
                // paragraph would be a line that lies.
                const statusLine =
                  entry.status === "streaming" && !entry.a
                    ? statusText(entry.statusLine)
                    : "";
                const isLast = i === lastIdx;
                const annotations = toolAnnotations(entry);
                return (
                  <div key={i} className="group space-y-3">
                    {/* User turn — right-aligned bubble. */}
                    <div className="flex justify-end">
                      <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-mainButtonBg/60 px-3.5 py-2 text-[13px] leading-relaxed text-mainText">
                        {entry.q}
                      </div>
                    </div>

                    {/* What the answer went and looked up. One quiet line PER
                        TOOL above the prose — provenance, not a status: it says
                        where part of this answer came from, in the same
                        register as the citation chips below it. Several tools
                        stack as several lines, tight (space-y-1) so the group
                        still reads as one aside; each line keeps the same
                        weight it had when there was only ever one. */}
                    {annotations.length > 0 && (
                      <div className="space-y-1">
                        {annotations.map(({ tool, queries }) => {
                          const { icon: Icon, label, quote } =
                            TOOL_ANNOTATION[tool] || UNKNOWN_ANNOTATION;
                          const text = queries
                            .map((query) => (quote ? `"${query}"` : query))
                            .join(" · ");
                          return (
                            <div
                              key={tool}
                              className="flex items-center gap-1.5 text-[11px] text-mutedText"
                            >
                              <Icon size={11} className="shrink-0" />
                              <span
                                className="truncate"
                                title={`${label}: ${text}`}
                              >
                                {label}: {text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Assistant turn — full width of the column. */}
                    <div className="prose prose-sm max-w-none break-words text-[13px] leading-relaxed text-mainText [&_a]:break-words [&_code]:break-words [&_li]:my-0.5 [&_ol]:my-2 [&_p]:my-2 [&_pre]:overflow-x-auto [&_table]:block [&_table]:overflow-x-auto [&_ul]:my-2">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={components}
                      >
                        {linkifyCitations(entry.a, citedNs, byN)}
                      </ReactMarkdown>
                      {entry.status === "streaming" && (
                        // THE SPINNER IS THE FEATURE and the line is a label
                        // on it: a server that never sends a status event
                        // renders exactly the bare spinner, and nothing here
                        // waits for an event that may never come.
                        <span className="inline-flex max-w-full items-center gap-1.5 align-middle">
                          <Loader2
                            size={13}
                            className="shrink-0 animate-spin text-velBlue"
                          />
                          {statusLine && (
                            <span className="truncate text-[11px] not-italic text-mutedText">
                              {statusLine}
                            </span>
                          )}
                        </span>
                      )}
                    </div>

                    {entry.status === "error" && entry.error && (
                      <div className="text-[12px] text-rose-400">
                        {entry.error}
                      </div>
                    )}

                    {/* Hover actions — always readable on the last turn, where
                        regenerate lives and has to be findable. */}
                    <div
                      className={`flex items-center gap-1.5 transition-opacity ${
                        isLast && !busy
                          ? "opacity-100"
                          : "opacity-0 focus-within:opacity-100 group-hover:opacity-100"
                      }`}
                    >
                      {entry.a && entry.status !== "streaming" && (
                        <button
                          type="button"
                          onClick={() => copyAnswer(entry, i)}
                          title="Copy answer (markdown, citations kept as [n])"
                          className="rounded-md p-1 text-mutedText transition hover:text-mainText"
                        >
                          {copied === i ? (
                            <Check size={12} className="text-emerald-400" />
                          ) : (
                            <Copy size={12} />
                          )}
                        </button>
                      )}
                      {isLast && !busy && entry.q && (
                        <button
                          type="button"
                          onClick={onRegenerate}
                          title="Ask this question again — the answer is appended, not replaced"
                          className="rounded-md p-1 text-mutedText transition hover:text-mainText"
                        >
                          <RotateCw size={12} />
                        </button>
                      )}
                      {entry.status === "stopped" && (
                        <span className="rounded-full border border-mainDivider px-1.5 text-[9px] uppercase tracking-[0.12em] text-mutedText">
                          stopped
                        </span>
                      )}
                      {entry.retrieval === "partial" && (
                        <span className="rounded-full border border-amber-400/40 px-1.5 text-[9px] uppercase tracking-[0.12em] text-amber-400">
                          partial sources
                        </span>
                      )}
                      {entry.truncated && (
                        <span className="rounded-full border border-amber-400/40 px-1.5 text-[9px] uppercase tracking-[0.12em] text-amber-400">
                          answer truncated
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Composer — anchored by the flex column, never by `fixed`. */}
      <div className="shrink-0 px-6 pb-5 pt-2">
        <div className="mx-auto w-full max-w-3xl">
          {/* Follow-up pills — quiet by design: they sit between the
              conversation and the composer, so they wrap rather than scroll,
              stay on one line each, and read as muted until hovered. */}
          {suggestions.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {suggestions.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => ask(q)}
                  title={q}
                  className="max-w-full truncate rounded-full border border-mainDivider px-3 py-1 text-[11px] text-mutedText transition hover:border-velBlue/50 hover:bg-mainButtonBg/35 hover:text-mainText"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          <Composer
            busy={busy}
            onAsk={ask}
            onStop={onStop}
            webSearch={webSearch}
            onWebSearchChange={onWebSearchChange}
            inputRef={composerRef}
            scopePicker={
              !isGS ? (
                <LensScopePicker
                  direction="up"
                  scope={scope}
                  scopeMissing={scopeMissing}
                  onChange={onScopeChange}
                  disabled={busy}
                />
              ) : null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default ThreadChatView;
